import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Copy, Eye, EyeOff, KeyRound, Loader2, RefreshCw, X } from 'lucide-react';
import { BACKEND_URL } from '../../config';
import { phoneDigits } from '../../utils/phone';
import { useOrganisations } from '../../utils/organisations';

/**
 * Accounts for a position rather than a person (caretaker, security desk, …).
 * They live on reserved numbers `+91 01XXXXXXXX` that no real phone can have
 * (see BReporter-Backend/src/utils/managedAccounts.ts). Real numbers are never
 * created here — their owners sign up themselves.
 *
 * Passwords are never stored: the backend returns the plaintext once, this page
 * shows it once, and after that it can only be reset.
 */

type ManagedUser = {
	id: number;
	name: string;
	phoneNumber: string | number;
	createdAt: string;
	isBlocked: boolean;
	is_authority: boolean;
	reportTemplate?: string;
	organisation?: string | null;
};

type Credentials = {
	title: string;
	name: string;
	phoneNumber: string;
	password: string;
};

type Option = { value: string; label: string };

const TEMPLATE_OPTIONS: Option[] = [
	{ value: 'default', label: 'Default' },
	{ value: 'caretaker', label: 'Caretaker' },
];

// Mirrors the backend rules (utils/validation.ts, auth password policy). The
// inputs strip anything outside these as it is typed, so bad characters never
// reach the API even if a server-side check is missed some day.
const NAME_MAX = 60;
const EMAIL_MAX = 320;
const MIN_PASSWORD = 8;
const MAX_PASSWORD = 64;
const onlyNameChars = (v: string) => v.replace(/[^A-Za-z .'-]/g, '').replace(/^[ .'-]+/, '').replace(/([ .'-])[ .'-]+/g, '$1');
const onlyEmailChars = (v: string) => v.replace(/[^A-Za-z0-9._%+@-]/g, '');

// What the holder types into the app's phone field: the 10 digits after +91.
const loginDigits = (raw: string | number) => {
	const d = phoneDigits(raw);
	return d.length === 12 && d.startsWith('91') ? d.slice(2) : d;
};

async function errorMessage(res: Response): Promise<string> {
	try {
		const data = await res.json();
		const msg = Array.isArray(data?.message) ? data.message.join(', ') : data?.message;
		if (msg) return msg;
	} catch {
		/* non-JSON error body */
	}
	return `Request failed (HTTP ${res.status})`;
}

// Same field language as the Organisations modal.
const labelCls = 'block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5 text-left';
const inputCls =
	'w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-900 focus:border-blue-400 focus:outline-none';
const boxCls = 'flex items-center w-full h-10 rounded-xl border border-gray-200 px-3 text-sm';
const checkLabelCls = 'inline-flex items-center gap-2 mt-2 text-sm font-semibold text-gray-700 cursor-pointer select-none';

const CopyButton = ({ text, label }: { text: string; label?: string }) => {
	const [copied, setCopied] = useState(false);
	return (
		<button
			type='button'
			onClick={() => {
				navigator.clipboard?.writeText(text).then(() => {
					setCopied(true);
					setTimeout(() => setCopied(false), 1500);
				});
			}}
			className='inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 h-8 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors shrink-0'
		>
			{copied ? <Check className='w-3.5 h-3.5 text-emerald-600' /> : <Copy className='w-3.5 h-3.5' />}
			{copied ? 'Copied' : label ?? 'Copy'}
		</button>
	);
};

const PasswordInput = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
	const [show, setShow] = useState(false);
	return (
		<div className='relative'>
			<input
				type={show ? 'text' : 'password'}
				value={value}
				onChange={(e) => onChange(e.target.value.replace(/\s/g, ''))}
				maxLength={MAX_PASSWORD}
				placeholder={`At least ${MIN_PASSWORD} characters`}
				autoComplete='new-password'
				className={`${inputCls} pr-10`}
			/>
			<button
				type='button'
				onClick={() => setShow((s) => !s)}
				className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
				aria-label={show ? 'Hide password' : 'Show password'}
			>
				{show ? <EyeOff className='w-4 h-4' /> : <Eye className='w-4 h-4' />}
			</button>
		</div>
	);
};

// Full-width select in the same style as the text inputs beside it.
const FieldSelect = ({ value, options, onChange }: { value: string; options: Option[]; onChange: (v: string) => void }) => {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!open) return;
		const close = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', close);
		return () => document.removeEventListener('mousedown', close);
	}, [open]);
	const selected = options.find((o) => o.value === value) ?? options[0];
	return (
		<div className='relative' ref={ref}>
			<button
				type='button'
				onClick={() => setOpen((o) => !o)}
				className={`${inputCls} flex items-center justify-between gap-2 bg-white text-left hover:border-blue-300`}
			>
				<span className='truncate'>{selected?.label}</span>
				<ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
			</button>
			{open && (
				<div className='absolute left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg z-[100]'>
					{options.map((o) => (
						<button
							type='button'
							key={o.value}
							onClick={() => {
								onChange(o.value);
								setOpen(false);
							}}
							className={`w-full text-left px-3 py-2 text-sm font-semibold hover:bg-blue-50 transition-colors ${o.value === value ? 'text-blue-700 bg-blue-50/60' : 'text-gray-700'}`}
						>
							{o.label}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

const Modal = ({ title, onClose, children }: { title: string; onClose?: () => void; children: React.ReactNode }) => (
	<div className='fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4'>
		<div className='bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md p-6 text-left'>
			<div className='flex items-center justify-between gap-2 mb-5'>
				<h2 className='text-xl font-extrabold text-blue-900 m-0'>{title}</h2>
				{onClose && (
					<button onClick={onClose} className='text-gray-400 hover:text-gray-700'>
						<X className='w-5 h-5' />
					</button>
				)}
			</div>
			{children}
		</div>
	</div>
);

// Shown exactly once after create / reset. Closing it is the point of no return.
const CredentialsModal = ({ creds, onClose }: { creds: Credentials; onClose: () => void }) => {
	const digits = loginDigits(creds.phoneNumber);
	const handout = `B-Reporter login for ${creds.name}\nPhone number: ${digits} (country code +91)\nPassword: ${creds.password}`;
	return (
		<Modal title={creds.title}>
			<div className='space-y-4'>
				<div>
					<div className={labelCls}>Phone number</div>
					<div className='flex items-center justify-between gap-2'>
						<span className='font-mono text-lg font-bold text-gray-900'>
							<span className='text-gray-400'>+91 </span>
							{digits}
						</span>
						<CopyButton text={digits} />
					</div>
				</div>
				<div>
					<div className={labelCls}>Password</div>
					<div className='flex items-center justify-between gap-2'>
						<span className='font-mono text-lg font-bold text-gray-900 break-all'>{creds.password}</span>
						<CopyButton text={creds.password} />
					</div>
				</div>
				<p className='text-xs text-amber-700 m-0'>Not stored anywhere. It can't be shown again, only reset.</p>
			</div>
			<div className='flex items-center gap-2 mt-6'>
				<CopyButton text={handout} label='Copy both' />
				<button
					onClick={onClose}
					className='ml-auto inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 h-9 font-bold text-white hover:bg-blue-700'
				>
					<Check className='w-4 h-4' /> Done
				</button>
			</div>
		</Modal>
	);
};

const ResetModal = ({
	user,
	onCancel,
	onDone,
}: {
	user: ManagedUser;
	onCancel: () => void;
	onDone: (creds: Credentials) => void;
}) => {
	const [ownPassword, setOwnPassword] = useState(false);
	const [password, setPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const submit = async () => {
		if (ownPassword && password.length < MIN_PASSWORD) {
			setError(`Password must be at least ${MIN_PASSWORD} characters.`);
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const res = await fetch(`${BACKEND_URL}/users/managed/${phoneDigits(user.phoneNumber)}/resetPassword`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(ownPassword ? { password } : {}),
			});
			if (!res.ok) {
				setError(await errorMessage(res));
				return;
			}
			const data = await res.json();
			onDone({ title: 'Password reset', name: user.name, phoneNumber: data.phoneNumber, password: data.password });
		} catch {
			setError('Could not reach the server.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal title='Reset password' onClose={submitting ? undefined : onCancel}>
			<p className='text-sm text-gray-600 m-0'>
				<span className='font-bold text-gray-900'>{user.name}</span> · +91 {loginDigits(user.phoneNumber)}
				<br />
				Signs the account out on every device.
			</p>
			<label className={checkLabelCls}>
				<input type='checkbox' checked={ownPassword} onChange={(e) => setOwnPassword(e.target.checked)} />
				Set the password myself
			</label>
			{ownPassword && (
				<div className='mt-2'>
					<PasswordInput value={password} onChange={setPassword} />
				</div>
			)}
			{error && <p className='text-sm font-semibold text-red-600 mt-3 mb-0'>{error}</p>}
			<div className='flex items-center gap-2 mt-6'>
				<button
					onClick={onCancel}
					disabled={submitting}
					className='ml-auto rounded-xl border border-gray-200 px-4 h-9 font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40'
				>
					Cancel
				</button>
				<button
					onClick={submit}
					disabled={submitting}
					className='inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 h-9 font-bold text-white hover:bg-red-700 disabled:opacity-60'
				>
					{submitting ? <Loader2 className='w-4 h-4 animate-spin' /> : <KeyRound className='w-4 h-4' />}
					Reset
				</button>
			</div>
		</Modal>
	);
};

const ManagedAccounts = () => {
	const orgs = useOrganisations();

	// ── Create form ──
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	// Assigned by the backend, never typed — this is only a preview of it.
	const [nextNumber, setNextNumber] = useState<string | null>(null);
	const [ownPassword, setOwnPassword] = useState(false);
	const [password, setPassword] = useState('');
	const [makeAuthority, setMakeAuthority] = useState(false);
	const [organisation, setOrganisation] = useState('none');
	const [template, setTemplate] = useState('default');
	const [submitting, setSubmitting] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	// ── List ──
	const [users, setUsers] = useState<ManagedUser[]>([]);
	const [loading, setLoading] = useState(true);
	const [listError, setListError] = useState<string | null>(null);
	const [filter, setFilter] = useState('');

	const [creds, setCreds] = useState<Credentials | null>(null);
	const [resetting, setResetting] = useState<ManagedUser | null>(null);

	const loadList = async () => {
		setLoading(true);
		setListError(null);
		try {
			const res = await fetch(`${BACKEND_URL}/users/managed/list`, { credentials: 'include' });
			if (!res.ok) {
				setListError(await errorMessage(res));
				return;
			}
			const data = await res.json();
			setUsers(Array.isArray(data) ? data : []);
		} catch {
			setListError('Could not reach the server.');
		} finally {
			setLoading(false);
		}
	};

	const loadNextNumber = async () => {
		try {
			const res = await fetch(`${BACKEND_URL}/users/managed/nextNumber`, { credentials: 'include' });
			if (!res.ok) return;
			const data = await res.json();
			setNextNumber(data?.phoneNumber ?? null);
		} catch {
			/* the field just stays on its placeholder */
		}
	};

	useEffect(() => {
		loadList();
		loadNextNumber();
	}, []);

	const orgOptions = useMemo<Option[]>(
		() => [{ value: 'none', label: 'None' }, ...orgs.map((o) => ({ value: o.key, label: o.key }))],
		[orgs]
	);

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);
		if (name.trim().length < 2) {
			setFormError('Enter a name.');
			return;
		}
		if (ownPassword && password.length < MIN_PASSWORD) {
			setFormError(`Password must be at least ${MIN_PASSWORD} characters.`);
			return;
		}

		setSubmitting(true);
		try {
			const res = await fetch(`${BACKEND_URL}/users/managed/create`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					name: name.trim(),
					email: email.trim() || undefined,
					password: ownPassword ? password : undefined,
					makeAuthority,
					organisation: makeAuthority ? organisation : undefined,
					template: makeAuthority ? template : undefined,
				}),
			});
			if (!res.ok) {
				setFormError(await errorMessage(res));
				return;
			}
			const data = await res.json();
			setCreds({ title: 'Account created', name: data.user.name, phoneNumber: data.user.phoneNumber, password: data.password });
			setName('');
			setEmail('');
			setPassword('');
			loadList();
			loadNextNumber();
		} catch {
			setFormError('Could not reach the server.');
		} finally {
			setSubmitting(false);
		}
	};

	const visibleUsers = useMemo(() => {
		const q = filter.trim().toLowerCase();
		if (!q) return users;
		const qDigits = phoneDigits(q);
		return users.filter(
			(u) => u.name?.toLowerCase().includes(q) || (qDigits !== '' && loginDigits(u.phoneNumber).includes(qDigits))
		);
	}, [users, filter]);

	return (
		<div className='max-w-6xl mx-auto px-4 pb-5'>
			<h1 className='text-3xl font-extrabold text-blue-900 mb-8'>Manage Accounts</h1>

			{/* Create */}
			<form onSubmit={submit} className='bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8 text-left'>
				<h2 className='text-xs font-bold uppercase tracking-wide text-gray-500 m-0 mb-5'>Create account</h2>

				<div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5'>
					<div>
						<label className={labelCls}>Name</label>
						<input
							value={name}
							onChange={(e) => setName(onlyNameChars(e.target.value))}
							maxLength={NAME_MAX}
							placeholder='e.g. Satpura Caretaker'
							className={inputCls}
						/>
					</div>

					<div>
						<label className={labelCls}>
							Email <span className='normal-case font-semibold text-gray-400'>(optional)</span>
						</label>
						<input
							value={email}
							onChange={(e) => setEmail(onlyEmailChars(e.target.value))}
							maxLength={EMAIL_MAX}
							type='email'
							className={inputCls}
						/>
					</div>

					<div>
						<label className={labelCls}>Phone number</label>
						<div className={`${boxCls} bg-gray-50 font-mono select-all`}>
							{nextNumber ? (
								<>
									<span className='text-gray-400'>+91&nbsp;</span>
									<span className='text-gray-900'>{loginDigits(nextNumber)}</span>
								</>
							) : (
								<span className='font-sans text-gray-400'>Assigned on create</span>
							)}
						</div>
					</div>

					<div>
						<label className={labelCls}>Password</label>
						{ownPassword ? (
							<PasswordInput value={password} onChange={setPassword} />
						) : (
							<div className={`${boxCls} bg-gray-50 text-gray-400`}>Generated on create</div>
						)}
						<label className={checkLabelCls}>
							<input
								type='checkbox'
								checked={ownPassword}
								onChange={(e) => {
									setOwnPassword(e.target.checked);
									setPassword('');
								}}
							/>
							Set the password myself
						</label>
					</div>

					<div className='md:col-span-2'>
						<label className={`${checkLabelCls} mt-0`}>
							<input type='checkbox' checked={makeAuthority} onChange={(e) => setMakeAuthority(e.target.checked)} />
							Make this an authority
						</label>
					</div>

					{makeAuthority && (
						<>
							<div>
								<label className={labelCls}>Organisation</label>
								<FieldSelect value={organisation} options={orgOptions} onChange={setOrganisation} />
							</div>
							<div>
								<label className={labelCls}>Report template</label>
								<FieldSelect value={template} options={TEMPLATE_OPTIONS} onChange={setTemplate} />
							</div>
						</>
					)}
				</div>

				<div className='flex items-center gap-3 mt-6'>
					{formError && <p className='text-sm font-semibold text-red-600 m-0'>{formError}</p>}
					<button
						type='submit'
						disabled={submitting}
						className='ml-auto inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 h-9 font-bold text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400'
					>
						{submitting ? <Loader2 className='w-4 h-4 animate-spin' /> : <Check className='w-4 h-4' />}
						Create
					</button>
				</div>
			</form>

			{/* List — same search bar + card grid as the Users tab */}
			<div className='bg-white p-2 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-2 mb-6'>
				<input
					type='text'
					className='flex-1 w-full pl-4 pr-3 py-1 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-gray-700 font-semibold h-[34px] text-sm'
					placeholder='Search accounts by name or number...'
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
				/>
				<div className='w-px h-6 bg-gray-200 mx-1'></div>
				<button
					className='bg-blue-600 hover:bg-blue-700 text-white px-3 rounded-xl font-bold transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 h-[34px] text-sm w-fit sm:w-[100px]'
					onClick={loadList}
					disabled={loading}
				>
					{loading ? (
						<Loader2 className='w-4 h-4 animate-spin' />
					) : (
						<>
							<RefreshCw className='w-4 h-4' /> <span className='hidden sm:inline'>Refresh</span>
						</>
					)}
				</button>
			</div>

			{listError && (
				<div className='mt-2 py-1.5 px-4 flex items-center justify-center rounded-xl bg-red-50 text-red-600 border border-red-100'>
					<p className='font-semibold text-center m-0 leading-none'>{listError}</p>
				</div>
			)}

			<div className='mt-4'>
				{loading && users.length === 0 ? (
					<div className='flex flex-col items-center justify-center py-20 opacity-50'>
						<Loader2 className='w-12 h-12 animate-spin text-blue-600 mb-4' />
					</div>
				) : visibleUsers.length === 0 ? (
					<p className='text-gray-500 font-medium py-10 m-0'>
						{users.length === 0 ? 'No accounts yet.' : 'No accounts match your search.'}
					</p>
				) : (
					<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
						{visibleUsers.map((u) => (
							<div
								key={u.id}
								className='bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all flex justify-between items-center gap-2'
							>
								<div className='text-left flex-1 min-w-0'>
									<h3 className='text-lg font-bold text-gray-900 truncate m-0' title={u.name}>
										{u.name}
									</h3>
									<p className='text-gray-500 font-medium text-sm m-0 truncate'>
										+91 {loginDigits(u.phoneNumber)}
										<span className='mx-1.5 text-gray-400'>•</span>
										<span className='font-normal text-gray-400 text-[12px]'>
											{new Date(u.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
										</span>
									</p>
								</div>
								<div className='flex items-center gap-1.5 shrink-0'>
									{u.is_authority && u.organisation && (
										<span className='bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap'>
											{u.organisation}
										</span>
									)}
									{u.is_authority && (
										<span className='bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap'>
											Authority
										</span>
									)}
									{u.isBlocked && (
										<span className='bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap'>
											Blocked
										</span>
									)}
									<button
										onClick={() => setResetting(u)}
										title='Reset password'
										className='h-[44px] px-3 bg-gray-50 hover:bg-red-50 hover:text-red-700 text-gray-600 rounded-[9px] transition-colors flex items-center gap-1.5 text-sm font-semibold focus:outline-none'
									>
										<KeyRound className='w-4 h-4' /> Reset
									</button>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{resetting && (
				<ResetModal
					user={resetting}
					onCancel={() => setResetting(null)}
					onDone={(c) => {
						setResetting(null);
						setCreds(c);
					}}
				/>
			)}
			{creds && <CredentialsModal creds={creds} onClose={() => setCreds(null)} />}
		</div>
	);
};

export default ManagedAccounts;
